import { IsotypeLogo } from "./IsotypeLogo";

// Malinois Writers lockup — built to the brand manual (Turno 7b/2b — Punto Final).
//   • "primary" — full lockup used 80% of the time: kicker + MALINOIS + row of
//     [Writers · thick rule · M as period]. This is the impactful signature.
//   • "horizontal" — header / signature: isotype · vertical divider · MALINOIS/Writers stacked.
//   • "vertical" — stationery / square formats: isotype on top, MALINOIS, rule, Writers.
//   • "wordmark" — text-only.
export function LogoLockup({
  variant = "primary",
  size = "md", // sm · md · lg · xl · hero
  invert = false,
  className = "",
  showKicker = true,
}) {
  const ink = invert ? "#FFFFFF" : "#14110F";
  const textColor = invert ? "text-paper" : "text-ink";
  // Hero-size lockups get centered on mobile & tablet, left-aligned on desktop.
  const align = size === "hero" ? "items-center lg:items-start text-center lg:text-left" : "items-start";

  // Size tokens — mirror the exact sizing recipes from the brand manual pages.
  const T = {
    sm: {
      malinois: "text-lg md:text-xl",
      writers: "text-[10px] md:text-xs",
      isotype: "h-6 md:h-7",
      kicker: "text-[9px]",
      rule: "h-[2px]",
      divider: "h-6 md:h-7",
      period: "h-4 md:h-[18px]",
    },
    md: {
      malinois: "text-2xl md:text-3xl",
      writers: "text-sm md:text-base",
      isotype: "h-8 md:h-10",
      kicker: "text-[10px]",
      rule: "h-[2px]",
      divider: "h-9 md:h-11",
      period: "h-5 md:h-6",
    },
    lg: {
      malinois: "text-4xl md:text-6xl",
      writers: "text-xl md:text-2xl",
      isotype: "h-12 md:h-16",
      kicker: "text-[11px]",
      rule: "h-[3px]",
      divider: "h-12 md:h-16",
      period: "h-7 md:h-8",
    },
    xl: {
      malinois: "text-6xl md:text-8xl",
      writers: "text-3xl md:text-5xl",
      isotype: "h-16 md:h-24",
      kicker: "text-xs",
      rule: "h-[3px] md:h-[4px]",
      divider: "h-16 md:h-20",
      period: "h-9 md:h-12",
    },
    hero: {
      malinois: "text-[3rem] sm:text-7xl md:text-8xl lg:text-[9rem] xl:text-[11rem]",
      writers: "text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl",
      isotype: "h-14 sm:h-20 md:h-24 lg:h-32 xl:h-40",
      kicker: "text-[10px] sm:text-xs md:text-sm",
      rule: "h-[3px] md:h-[4px] lg:h-[6px]",
      divider: "h-16 sm:h-20 md:h-24 lg:h-32",
      period: "h-7 sm:h-10 md:h-12 lg:h-16 xl:h-20",
    },
  };
  const s = T[size];

  const kicker = showKicker && (
    <span
      className={`block font-body font-semibold uppercase tracking-[0.3em] text-accent ${s.kicker}`}
    >
      Trained for your brand’s mission
    </span>
  );

  // Style objects for Bodoni-Moda-specific typography — kept inline to
  // guarantee exact rendering regardless of Tailwind class order.
  const MALINOIS_STYLE = {
    fontFamily: '"Bodoni Moda", "Playfair Display", serif',
    fontWeight: 900,
    letterSpacing: "-0.03em",
    lineHeight: 0.88,
    color: ink,
  };
  const WRITERS_STYLE = {
    fontFamily: '"Bodoni Moda", "Playfair Display", serif',
    fontStyle: "italic",
    fontWeight: 400,
    color: ink,
    lineHeight: 1,
  };

  if (variant === "primary" || variant === "vertical") {
    return (
      <div className={`inline-flex flex-col ${align} ${className}`} data-testid="logo-lockup">
        {kicker}
        <span style={MALINOIS_STYLE} className={`${s.malinois} ${showKicker ? "mt-3 md:mt-4" : ""}`}>
          MALINOIS
        </span>
        <div className="flex items-center gap-3 md:gap-4 w-full mt-2 md:mt-3">
          <span style={WRITERS_STYLE} className={s.writers}>
            Writers
          </span>
          <span
            className={`flex-1 ${s.rule}`}
            style={{ background: ink }}
            aria-hidden="true"
          />
          <IsotypeLogo className={`${s.period} shrink-0`} invert={invert} />
        </div>
      </div>
    );
  }

  if (variant === "wordmark") {
    return (
      <div className={`inline-flex flex-col ${align} ${className}`} data-testid="logo-lockup">
        {kicker}
        <span style={MALINOIS_STYLE} className={`${s.malinois} ${showKicker ? "mt-2" : ""}`}>
          MALINOIS
        </span>
        <span style={WRITERS_STYLE} className={`${s.writers} mt-1`}>
          Writers
        </span>
      </div>
    );
  }

  // horizontal — header + email signature (5b)
  return (
    <div className={`inline-flex items-center gap-3 md:gap-4 ${className}`} data-testid="logo-lockup">
      <IsotypeLogo className={s.isotype} invert={invert} />
      <span
        className={`shrink-0 w-[2px] ${s.divider}`}
        style={{ background: ink, opacity: invert ? 0.4 : 0.25 }}
        aria-hidden="true"
      />
      <div className="flex flex-col">
        <span style={MALINOIS_STYLE} className={s.malinois}>
          MALINOIS
        </span>
        <span style={WRITERS_STYLE} className={`${s.writers} mt-0.5 md:mt-1`}>
          Writers
        </span>
      </div>
    </div>
  );
}
